/* eslint-disable lingui/no-unlocalized-strings */
import { t } from "@lingui/core/macro";
import { assertNever, localStorageSetTyped, RegrelloLocalStorageKey } from "@regrello/core-utils";
import { RegrelloLinkV2 } from "@regrello/ui-core";
import React, { type FormEvent, useCallback, useMemo, useState } from "react";
import { RegrelloRestApiService } from "../../../../services/RegrelloRestApiService";
import { RoutePaths, RouteQueryStringKeys } from "../../../../ui2/app/routes/consts";
import { useQueryMap } from "../../../../utils/hooks/useQueryStrings";
import {
  parseJwtClaim,
  type RegrelloGranularAccessJWTClaim,
  RegrelloGranularAccessJWTClaimKeys,
} from "../../../../utils/jwtUtil";
import { UnauthenticatedPageLayout } from "../../../auth/unauthenticatedPages/_internal/UnauthenticatedPageLayout";
import { PersonalizedLogo } from "../_internal/PersonalizedLogo";
import { ErrorContent } from "./_internal/ErrorContent";
import { SuccessContent } from "./_internal/SuccessContent";
import { TeamEmailContent } from "./_internal/TeamEmailContent";
import { VerificationContent } from "./_internal/VerificationContent";

type EmailSentState = "IDLE" | "PENDING" | "SUCCESS" | "NEEDS_EMAIL" | "ERROR";

const STATUS_BAD_REQUEST = 400;
const STATUS_NEED_TEAM_EMAIL = 432;
const STATUS_OK = 200;

/**
 * A page where an email user can verify their email address with Regrello, allowing them to access
 * any subsequent Regrello webform from their current device.
 */
export const VerificationPage = React.memo(function VerificationPageFn() {
  const { [RouteQueryStringKeys.SUPPLIER]: supplierQueryValue, [RouteQueryStringKeys.TOKEN]: granularAccessToken } =
    useQueryMap();
  const queryParams = new URLSearchParams();
  if (supplierQueryValue != null) {
    queryParams.set(RouteQueryStringKeys.SUPPLIER, supplierQueryValue);
  }
  const signInButtonHref = `${RoutePaths.LOGIN}?${queryParams.toString()}`;

  const emailFromJWT = useMemo(() => {
    if (granularAccessToken == null) {
      return "";
    }
    const jwtClaim = parseJwtClaim<RegrelloGranularAccessJWTClaim>(granularAccessToken);
    return jwtClaim[RegrelloGranularAccessJWTClaimKeys.userEmail] ?? "";
  }, [granularAccessToken]);

  const [emailSentStatus, setEmailSentStatus] = useState<EmailSentState>(emailFromJWT !== "" ? "IDLE" : "ERROR");
  const [emailForTeam, setEmailForTeam] = useState<string | null>(null);

  const requestVerification = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setEmailSentStatus("PENDING");
      const { status } = await RegrelloRestApiService.requestVerificationLink(granularAccessToken, emailForTeam);
      switch (status) {
        case STATUS_OK:
          setEmailSentStatus("SUCCESS");
          localStorageSetTyped(RegrelloLocalStorageKey.VERIFICATION_TIMESTAMP, Date.now().toString());
          break;
        case STATUS_BAD_REQUEST:
          setEmailSentStatus("ERROR");
          break;
        case STATUS_NEED_TEAM_EMAIL:
          setEmailSentStatus("NEEDS_EMAIL");
          break;
        default:
          setEmailSentStatus("ERROR");
      }
    },
    [emailForTeam, granularAccessToken],
  );

  const currentContent = useMemo(() => {
    const isLoading = emailSentStatus === "PENDING";

    switch (emailSentStatus) {
      case "IDLE":
      case "PENDING":
        return (
          <VerificationContent email={emailFromJWT} isLoading={isLoading} requestVerification={requestVerification} />
        );
      case "SUCCESS":
        return <SuccessContent email={emailForTeam ?? emailFromJWT} />;
      case "NEEDS_EMAIL":
        return (
          <TeamEmailContent
            isLoading={isLoading}
            requestVerification={requestVerification}
            setEmail={setEmailForTeam}
          />
        );
      case "ERROR":
        return <ErrorContent isLoading={isLoading} requestVerification={requestVerification} />;
      default:
        assertNever(emailSentStatus);
    }
  }, [emailForTeam, emailFromJWT, emailSentStatus, requestVerification]);

  return (
    <UnauthenticatedPageLayout>
      <div className="p-10 w-160">
        <header className="flex justify-between items-center p-4">
          <PersonalizedLogo granularAccessToken={granularAccessToken} />

          <RegrelloLinkV2 className="font-bold text-primary-textMuted" to={signInButtonHref}>
            {t`Sign in`}
          </RegrelloLinkV2>
        </header>
        <hr />

        {currentContent}
      </div>
    </UnauthenticatedPageLayout>
  );
});
